import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight } from 'lucide-react';
import { getBlogArticles } from '@/lib/api';

interface BlogArticle {
  id: string;
  title: string;
  preview: string;
  tags: string[];
  created_at: string;
}

const LatestBlogPosts = () => {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLatestArticles();
  }, []);

  const loadLatestArticles = async () => {
    try {
      const data = await getBlogArticles();
      // Get top 3 latest articles
      const latest = (data || []).slice(0, 3);
      setArticles(latest);
    } catch (error) {
      console.error('Error loading latest articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading || articles.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-navy mb-4">
              Neueste Blog-Artikel
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              IT-Tipps, Anleitungen und Wissenswertes für Ihren digitalen Alltag
            </p>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {articles.map(article => (
              <Link
                key={article.id}
                to={`/blog/${article.id}`}
                className="group relative bg-card rounded-3xl border border-navy/10 overflow-hidden shadow-soft hover:shadow-lift transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/35"
              >
                {/* Leiterbahn-Punkt */}
                <span className="absolute top-6 right-6 h-2.5 w-2.5 rounded-full border-2 border-flame/45 transition-colors group-hover:border-flame" aria-hidden="true" />

                <div className="p-6">
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(article.tags || []).slice(0, 2).map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-navy mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {article.title}
                  </h3>

                  {/* Date */}
                  <div className="flex items-center text-sm text-muted-foreground mb-3">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatDate(article.created_at)}
                  </div>

                  {/* Preview */}
                  <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
                    {article.preview}
                  </p>

                  {/* Read More Link */}
                  <div className="flex items-center text-primary font-bold text-sm">
                    Weiterlesen
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center">
            <Link
              to="/blog"
              className="inline-flex items-center px-8 py-3.5 bg-primary text-white rounded-full hover:bg-primary/90 transition-all font-bold shadow-soft hover:-translate-y-0.5"
            >
              Alle Artikel anzeigen
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestBlogPosts;
